import type {
  ExtractionUploadResponse,
  ListExtractionTasksResponse,
} from '@store/generated/documents';
import { enhancedApi } from '@store/generated/documents';




/**
 * Feature-specific tag types for documents
 */
const DOCUMENTS_TAG_TYPES = ['Document', 'DocumentList', 'ExtractionStatus'] as const;

/**
 * Documents API - extends the generated API with custom tagging
 */
export const documentsApi = enhancedApi
  .enhanceEndpoints({
  addTagTypes: DOCUMENTS_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Extraction endpoints - overriding generated ones to support FormData/polling
      postV2ProviderDocumentsExtract: builder.mutation<ExtractionUploadResponse, { files: File[] }>({
        query: ({ files }) => {
          const formData = new FormData();
          files.forEach(file => {
            formData.append('files[]', file);
          });
          return {
            url: '/v2/provider/documents/extract',
            method: 'POST',
            body: formData,
          };
        },
      }),
      // Override tasks endpoint to support limit query parameter
      getV2ProviderDocumentsExtractTasks: builder.query<
        ListExtractionTasksResponse,
        { limit?: number }
      >({
        query: ({ limit = 5 } = {}) => ({
          url: '/v2/provider/documents/extract/tasks',
          params: limit ? { limit } : undefined,
        }),
      }),
      // Document proxy endpoint that returns Blob directly
      getDocumentProxyBlob: builder.query<
        Blob,
        { documentId: string; disposition?: 'inline' | 'attachment' }
      >({
        query: ({ documentId, disposition = 'inline' }) => ({
          url: `/v2/provider/documents/proxy/${documentId}`,
          params: { disposition },
          responseHandler: async (response) => {
            if (!response.ok) {
              // For errors, parse as JSON (backend returns JSON errors)
              const error = await response.json();
              throw new Error(error.message || 'Failed to fetch document');
            }
            return response.blob();
          },
        }),
        // Don't cache blobs in RTK Query (they're large and we create blob URLs)
        keepUnusedDataFor: 0,
      }),
      // Note: Both approve and reject endpoints are now using the generated endpoints
      // Approve expects: { taskId: string; approveRulesRequest: ApproveRulesRequest }
      // Reject expects: { taskId: string; rejectRulesRequest: RejectRulesRequest }
    }),
    overrideExisting: true,
  })
  .enhanceEndpoints({
  endpoints: {
    // Provider documents list
    getV2ProviderDocuments: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'Document' as const,
                id: item.document_id,
              })),
              { type: 'DocumentList' as const, id: 'LIST' },
            ]
          : [{ type: 'DocumentList', id: 'LIST' }],
    },
    // Single document by ID
    getV2ProviderDocumentsByDocumentId: {
      providesTags: (_result, _error, arg) => [{ type: 'Document' as const, id: arg.documentId }],
    },
    // Create document for encounter
    postV2ProviderEncountersByEncounterIdDocuments: {
      invalidatesTags: [{ type: 'DocumentList', id: 'LIST' }],
    },
    // Update document
    patchV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Document' as const, id: arg.documentId },
        { type: 'DocumentList', id: 'LIST' },
      ],
    },
    // Delete document
    deleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Document' as const, id: arg.documentId },
        { type: 'DocumentList', id: 'LIST' },
      ],
    },
    // Generate and send document
    postV2ProviderDocumentsGenerateAndSend: {
      invalidatesTags: [{ type: 'DocumentList', id: 'LIST' }],
    },
      // Extraction status - provides tag for cache invalidation
      getV2ProviderDocumentsExtractByTaskIdStatus: {
        providesTags: (_result, _error, arg) => [
          { type: 'ExtractionStatus' as const, id: arg.taskId },
        ],
      },
  },
});

// Export hooks with user-friendly names
export const {
  useGetV2ProviderDocumentsQuery: useGetDocumentsQuery,
  useGetV2ProviderDocumentsByDocumentIdQuery: useGetDocumentQuery,
  useLazyGetV2ProviderDocumentsByDocumentIdQuery: useLazyGetDocumentQuery,
  usePostV2ProviderEncountersByEncounterIdDocumentsMutation: useCreateDocumentMutation,
  usePatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useUpdateDocumentMutation,
  useDeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useDeleteDocumentMutation,
  usePostV2ProviderDocumentsGenerateAndSendMutation: useGenerateAndSendDocumentMutation,
  // Extraction hooks
  usePostV2ProviderDocumentsExtractMutation: useUploadExtractionFilesMutation,
  useGetV2ProviderDocumentsExtractByTaskIdStatusQuery: useGetExtractionStatusQuery,
  useLazyGetV2ProviderDocumentsExtractByTaskIdStatusQuery: useLazyGetExtractionStatusQuery,
  useGetV2ProviderDocumentsExtractTasksQuery: useGetExtractionTasksQuery,
  // Document proxy blob hooks
  useLazyGetDocumentProxyBlobQuery,
} = documentsApi;
